import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAccountMappingHeads, createAccountMapping, putAccountMapping, getAccountMapping, getAccounts, deleteAccountMapping, putAccountMappingName, postPublishAccountMappings } from "@/services/account-mapping-api";
import { Account, AccountMapping, AccountMappingHead, NewAccountMapping } from "@/types/account-mapping-api";

export const useAccountMappingHeads = (clientId: string) => {

    const {data: accountMappingHeads, isLoading: isLoadingAccountMappingHeads, error: accountMappingHeadsError } =  useQuery<Array<AccountMappingHead>>({
        queryKey: ['accountMappingHeads', clientId],
        queryFn: () => getAccountMappingHeads(clientId),
            enabled: Boolean(clientId),
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
      });
    
    return {accountMappingHeads, isLoadingAccountMappingHeads, accountMappingHeadsError}
};

export const useCreateAccountMapping = (clientId: string) => {
	const queryClient = useQueryClient();

  const { mutate: createNewAccountMapping, isPending: isCreating, isSuccess: createSuccess, error: createError, data: createdMapping } = useMutation({
    mutationFn: (newAccountMapping: NewAccountMapping) => createAccountMapping(newAccountMapping),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accountMappingHeads', clientId] }); // Refresh list
    },
  });
    
	return { createNewAccountMapping, isCreating, createSuccess, createError, createdMapping}
};

export const useAccountMapping = (accountMappingId: string | null) => {
    const {data: selectedAccountMapping, isLoading: isLoadingAccountMapping, error: accountMappingError} = useQuery<AccountMapping>({
      queryKey: ['accountMapping', accountMappingId],
      queryFn: () =>  getAccountMapping(accountMappingId),
      enabled: Boolean(accountMappingId),
      staleTime: 0,
      gcTime: 0,
      retry: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });
    return {selectedAccountMapping, isLoadingAccountMapping, accountMappingError};
};

export const useAccounts = (clientId: string) => {
    const {data: accounts, isLoading: isLoadingAccounts, error: accountsError } =  useQuery<Array<Account>>({
        queryKey: ['accounts', clientId],
        queryFn: () => getAccounts(clientId),
            enabled: Boolean(clientId),
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
    });
    
    return {accounts, isLoadingAccounts, accountsError}
}

export const useUpdateAccountMapping = (accountMapping: AccountMapping) => {
    const queryClient = useQueryClient();

  const { mutate: updateAccountMapping, isPending: isUpdating, isSuccess: updateSuccess, error: updateError } = useMutation<AccountMapping, Error, AccountMapping>({
    mutationFn: (accountMapping: AccountMapping) => putAccountMapping(accountMapping),
            onSuccess: (data) => {
                // Directly update the cached data with the response
                queryClient.setQueryData(['accountMapping', accountMapping.id], data);
                queryClient.invalidateQueries({ queryKey: ['accountMappingHeads', accountMapping.clientId] }); // Refresh list
            },
        }
    );
    
    return { updateAccountMapping, isUpdating, updateSuccess, updateError}
}

export const useDeleteAccountMapping = (accountMappingId: string) => {
    const queryClient = useQueryClient();

  const { mutate: deleteMapping, isPending: isDeleting, isSuccess: deleteSuccess, error: deleteError } = useMutation({
    mutationFn: (accountMappingId: string) => deleteAccountMapping(accountMappingId),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['accountMapping', accountMappingId] });
                queryClient.invalidateQueries({ queryKey: ['accountMappingHeads'] }); // Refresh list
            },
        }
    );
    
    return { deleteMapping, isDeleting, deleteSuccess, deleteError}
}

export const useUpdateAccountMappingName = (accountMappingId: string) => {
    const queryClient = useQueryClient();

  const { mutate: updateAccountMappingName, isPending: isUpdatingMappingName, isSuccess: updateMappingNameSuccess, error: updateMappingNameError } = useMutation({
    mutationFn: (name: string) => putAccountMappingName(accountMappingId, name),
            onSuccess: (data) => {
                // Update only the name property while keeping the rest of the data intact
                queryClient.setQueryData(['accountMapping', accountMappingId], (oldData: AccountMapping | undefined) => {
                    return oldData ? { ...oldData, name: data.name } : data;
                });
                queryClient.invalidateQueries({ queryKey: ['accountMappingHeads'] }); // Refresh list
            },
        }
    );
    
    return { updateAccountMappingName, isUpdatingMappingName, updateMappingNameSuccess, updateMappingNameError}
}

export const usePublishAccountMappings = (clientId: string) => {
    const queryClient = useQueryClient();

  const { mutate: publishAccountMappings, isPending: isPublishing, isSuccess: publishSuccess, error: publishError } = useMutation({
    mutationFn: () => postPublishAccountMappings(clientId),
        }
    );
    
    return { publishAccountMappings, isPublishing, publishSuccess, publishError}
}